/**
 * src/shell/modules/SovereignModuleSlotRenderer.tsx
 *
 * Micro-Frontend Slot Container with Error Isolation (Sprint 3 / Issue 3.2).
 * Renders dynamically registered module components for target UI slots.
 */

import React, { useState, useEffect, Component, ReactNode, ErrorInfo } from 'react';
import { ModuleSlotEntry, sovereignModuleSlotManager } from './SovereignModuleSlotManager.js';
import { ModuleSandboxBridge } from './ModuleSandboxBridge.js';
import { AlertTriangle } from 'lucide-react';

interface SovereignModuleSlotRendererProps {
  slot: 'header' | 'sidebar' | 'footer' | 'floating' | 'room' | 'main:pane' | 'top:split';
  className?: string;
  activeModuleId?: string;
  fallback?: ReactNode;
}

class SlotErrorBoundary extends Component<
  { moduleId: string; children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { moduleId: string; children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(`[SlotErrorBoundary] Crash in module ${this.props.moduleId}:`, error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-mono">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="truncate">Mod Error ({this.props.moduleId})</span>
        </div>
      );
    }
    return this.props.children;
  }
}

export const SovereignModuleSlotRenderer: React.FC<SovereignModuleSlotRendererProps> = ({
  slot,
  className,
}) => {
  const [entries, setEntries] = useState<ModuleSlotEntry[]>([]);
  const [activeModalId, setActiveModalId] = useState<string | null>(null);

  useEffect(() => {
    const unsub = sovereignModuleSlotManager.subscribe((slotMap) => {
      setEntries(slotMap.get(slot) || []);
    });
    const unsubModal = sovereignModuleSlotManager.subscribeModal((modalId) => {
      setActiveModalId(modalId);
    });

    return () => {
      unsub();
      unsubModal();
    };
  }, [slot]);

  // Special behavior for floating modal overlay slot
  if (slot === 'floating') {
    if (!activeModalId) return null;
    const activeEntry = entries.find((e) => e.moduleId === activeModalId);
    if (!activeEntry) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
        <div
          className="fixed inset-0"
          onClick={() => sovereignModuleSlotManager.closeModuleModal()}
        />
        <div className="relative z-10">
          <SlotErrorBoundary moduleId={activeEntry.moduleId}>
            <ModuleSandboxBridge entry={activeEntry} slot="floating" />
          </SlotErrorBoundary>
        </div>
      </div>
    );
  }

  if (entries.length === 0) return null;

  if (slot === 'sidebar') {
    return (
      <div className={className || 'flex flex-col gap-1.5'}>
        <div style={{
          fontSize: '10px',
          fontWeight: 800,
          color: 'var(--theme-text-muted, #64748b)',
          textTransform: 'uppercase',
          marginBottom: '3px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          letterSpacing: '0.05em',
        }}>
          <span>Sovereign Modules</span>
          <span style={{
            fontSize: '9px',
            color: 'var(--theme-accent, #38bdf8)',
            background: 'var(--theme-accent-subtle, rgba(56,189,248,0.12))',
            padding: '1px 5px',
            borderRadius: '4px',
            fontWeight: 700,
          }}>SANDBOXED</span>
        </div>
        {entries.map((entry) => (
          <SlotErrorBoundary key={`${entry.moduleId}_${entry.slot}`} moduleId={entry.moduleId}>
            <ModuleSandboxBridge entry={entry} slot={slot} />
          </SlotErrorBoundary>
        ))}
      </div>
    );
  }

  return (
    <div className={className || 'flex items-center gap-2'}>
      {entries.map((entry) => (
        <SlotErrorBoundary key={`${entry.moduleId}_${entry.slot}`} moduleId={entry.moduleId}>
          <ModuleSandboxBridge entry={entry} slot={slot} />
        </SlotErrorBoundary>
      ))}
    </div>
  );
};
